/**
 * @fileoverview Recent Users LocalStorage Utilities
 *
 * Handles storing and retrieving recently logged in users for quick login functionality.
 */

const RECENT_USERS_KEY = 'clockwise_recent_users';
const MAX_RECENT_USERS = 5;

/**
 * Get all recent users from localStorage
 * @returns {Array} Array of recent user objects
 */
export const getRecentUsers = () => {
  try {
    const stored = localStorage.getItem(RECENT_USERS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Add a user to the recent users list
 * @param {Object} user - User object with username, firstName, lastName, hasPasskey
 */
export const addRecentUser = (user) => {
  try {
    const recent = getRecentUsers();
    // Remove existing entry for this user
    const filtered = recent.filter(u => u.username !== user.username);
    // Add to front with timestamp
    const updated = [{
      username: user.username,
      firstName: user.first_name || user.firstName || '',
      lastName: user.last_name || user.lastName || '',
      hasPasskey: user.hasPasskey || false,
      lastLogin: new Date().toISOString()
    }, ...filtered].slice(0, MAX_RECENT_USERS);
    localStorage.setItem(RECENT_USERS_KEY, JSON.stringify(updated));
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Remove a user from the recent users list
 * @param {string} username - Username to remove
 */
export const removeRecentUser = (username) => {
  try {
    const recent = getRecentUsers();
    const filtered = recent.filter(u => u.username !== username);
    localStorage.setItem(RECENT_USERS_KEY, JSON.stringify(filtered));
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Update a specific user's passkey status
 * @param {string} username - Username to update
 * @param {boolean} hasPasskey - New passkey status
 */
export const updateUserPasskeyStatus = (username, hasPasskey) => {
  try {
    const recent = getRecentUsers();
    const updated = recent.map(u =>
      u.username === username ? { ...u, hasPasskey } : u
    );
    localStorage.setItem(RECENT_USERS_KEY, JSON.stringify(updated));
    return updated;
  } catch {
    return getRecentUsers();
  }
};
